import { QueryClientProvider } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { ReactNode } from "react";
import { ApiClientProvider } from "../api/ApiClientContext";
import type { ApiClient } from "../api/client";
import type { AuthClient } from "../auth/auth-client";
import { AuthSessionProvider } from "../auth/AuthSessionProvider";
import { AppErrorBoundary } from "../pages/errors/AppErrorBoundary";
import { createQueryClient } from "./query-client";
import { SessionProvider } from "./session/SessionContext";
import type { Session } from "./session/session";

interface AppProvidersProps {
  children: ReactNode;
  /** Tests inject a client; the app creates one per mount. */
  queryClient?: QueryClient;
  /** Static session for UI tests. Ignored when `authClient` is given. Omitted = anonymous. */
  session?: Session;
  /** Live Firebase session (main.tsx). Takes precedence over `session`. */
  authClient?: AuthClient;
  /** Created in main.tsx from validated config. Optional so pure-UI tests need no network. */
  apiClient?: ApiClient;
}

/** Provider stack (WEB-005). Error boundary is outermost so provider failures are caught too. */
export function AppProviders({
  children,
  queryClient,
  session,
  apiClient,
  authClient,
}: AppProvidersProps) {
  const [client] = useState(() => queryClient ?? createQueryClient());
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={client}>
        {authClient ? (
          <AuthSessionProvider client={authClient}>
            {withApi(apiClient, children)}
          </AuthSessionProvider>
        ) : (
          <SessionProvider {...(session ? { session } : {})}>
            {withApi(apiClient, children)}
          </SessionProvider>
        )}
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}

function withApi(apiClient: ApiClient | undefined, children: ReactNode) {
  return apiClient ? (
    <ApiClientProvider client={apiClient}>{children}</ApiClientProvider>
  ) : (
    children
  );
}
